import React, {useMemo, useState} from 'react';
import {makeStyles} from '@mui/styles';
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
} from '@mui/material';
import {FormattedMessage} from 'react-intl';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import {Formik} from 'formik';

import FormikInput from '../../../../components/input/formikInput';
import {
    borderGrayColor,
    dangerColor,
    grayColor,
    infoColor,
    infoColorDisabled,
    infoColorHover,
    whiteColorDisabled,
} from '../../../../assets/jss/main';

import updateShipmentAddress from '../../../../api/shipmentAddress/updateShipmentAddress';
import createShipmentAddress from '../../../../api/shipmentAddress/createShipmentAddress';
import ModalRelayPointPicker from './ModalRelayPointPicker';
import {isRelayPointChangeable, NoTracking} from '../../../../utils/ShippingMethods';
import {ShipmentStatus} from '../../../../utils/ShipmentStatus';

const useStyles = makeStyles(theme => ({
    paper: {
        display: 'inline-flex',
        padding: 24,
        flexDirection: 'column',
        alignItems: 'flex-end',
        justifyContent: 'flex-end',
        boxShadow: '0px 4px 22px 0px rgba(0, 0, 0, 0.08)',
        borderRadius: 8,
        '& .MuiDialogContent-root': {
            padding: 0,
            marginBottom: 48,
        },
        '& .MuiDialogContent-root h2': {
            padding: 0,
            margin: 0,
            marginBottom: '32px',
            color: '#333',
            fontSize: '20px',
            fontWeight: 700,
            lineHeight: '28px',
            letterSpacing: '0.15px',
        },
        '& .MuiDialogActions-root > :not(:first-of-type) ': {
            marginLeft: '12px',
        },
        '& .MuiDialogActions-root': {
            padding: 0,
            justifyContent: 'flex-end',
        },
    },
    form: {
        display: 'flex',
        alignItems: 'flex-start',
        alignContent: 'flex-start',
        gap: 16,
        alignSelf: 'stretch',
        flexWrap: 'wrap',
        '& div': {
            width: 'calc(50% - 8px)',
            margin: 0,
            padding: 0,
            '& label': {
                marginBottom: 4,
                color: '#333',
                fontSize: '12px',
                fontStyle: 'normal',
                fontWeight: 600,
                lineHeight: 'normal',
                '&[data-disabled="true"]': {
                    color: grayColor,
                },
            },
            '& input': {
                padding: 16,
                borderRadius: 8,
                border: `1px solid ${borderGrayColor}`,
                background: '#FFF',
                fontSize: '14px',
                fontStyle: 'normal',
                fontWeight: 400,
                lineHeight: 'normal',
                '&:disabled': {
                    background: whiteColorDisabled,
                    color: grayColor,
                },
                '&:focus': {
                    outline: 0,
                    borderRadius: 8,
                    borderColor: infoColor,
                },
            },
            '& > span': {
                fontSize: '14px',
                color: dangerColor,
            },
        },
    },
    errorMessage: {
        fontSize: '14px',
        color: dangerColor,
        marginTop: 8,
    },
    relayPointRow: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 16,
        padding: 12,
        marginBottom: 16,
        borderRadius: 8,
        background: '#F7F7F7',
        '& p': {
            margin: 0,
            fontSize: '13px',
            color: '#555',
        },
        '& strong': {
            color: '#333',
        },
    },
    actionButton: {
        padding: '12px 16px',
        color: '#333',
        fontSize: '14px',
        lineHeight: '14px',
        fontWeight: 400,
        textTransform: 'none',
        borderRadius: 8,
        border: `1px solid ${borderGrayColor}`,
        '&:hover': {
            backgroundColor: '#F1F1F1',
        },
    },
    validatedButton: {
        color: '#FFF',
        backgroundColor: infoColor,
        '&:hover': {
            backgroundColor: infoColorHover,
        },
        '&.Mui-disabled': {
            color: '#FFF',
            backgroundColor: infoColorDisabled,
        },
    },
}));

/**
 * A relay point can only be swapped while no carrier label exists: once a tracking number or a
 * waybill document is there the API answers 409, so the button is not even shown.
 */
function canChangeRelayPoint(shipment) {
    if (!isRelayPointChangeable(shipment)) {
        return false;
    }
    if (shipment?.status === ShipmentStatus.SHIPPED || shipment?.shippedAt) {
        return false;
    }
    if (shipment?.trackingNumber && shipment.trackingNumber !== NoTracking) {
        return false;
    }
    // `documents` comes either as a JSON-LD array or as an object map (see TrackingNumbers.js),
    // Object.values() copes with both.
    const documents = shipment?.documents ? Object.values(shipment.documents) : [];

    return !documents.some(document => document?.type === 'waybill');
}

export default function ModalShipmentAddressEdit({open, onClose, shipment, refreshOrder}) {
    const [errorMessageId, setErrorMessageId] = useState('');
    const [relayPointPickerOpen, setRelayPointPickerOpen] = useState(false);
    const classes = useStyles();
    const showRelayPointButton = canChangeRelayPoint(shipment);

    const initialValues = useMemo(() => ({
        firstname: shipment?.deliveryFirstname || '',
        lastname: shipment?.deliveryLastname || '',
        company: shipment?.deliveryCompany || '',
        address: shipment?.deliveryAddress || '',
        addressMore: shipment?.deliveryAddressMore || '',
        postalCode: shipment?.deliveryPostalCode || '',
        city: shipment?.deliveryCity || '',
        country: shipment?.deliveryCountry || '',
        phone: shipment?.deliveryPhone || '',
        email: shipment?.deliveryEmail || '',
    }), [shipment]);

    const validate = values => {
        const errors = {};

        if (!values.firstname || values.firstname.trim() === '') {
            errors.firstname = <FormattedMessage id={'picking.order.show.customerShippingEdit.form.error.firstname.missing'} />;
        }

        if (!values.lastname || values.lastname.trim() === '') {
            errors.lastname = <FormattedMessage id={'picking.order.show.customerShippingEdit.form.error.lastname.missing'} />;
        }

        if (!values.address || values.address.trim() === '') {
            errors.address = <FormattedMessage id={'picking.order.show.customerShippingEdit.form.error.address.missing'} />;
        }
        if (!values.postalCode || values.postalCode.trim() === '') {
            errors.postalCode = <FormattedMessage id={'picking.order.show.customerShippingEdit.form.error.postalCode.missing'} />;
        }
        if (!values.city || values.city.trim() === '') {
            errors.city = <FormattedMessage id={'picking.order.show.customerShippingEdit.form.error.city.missing'} />;
        }

        if (!values.country || values.country.trim() === '') {
            errors.country = <FormattedMessage id={'picking.order.show.customerShippingEdit.form.error.country.missing'} />;
        }

        if (!values.phone || values.phone.trim() === '') {
            errors.phone = <FormattedMessage id={'picking.order.show.customerShippingEdit.form.error.phone.missing'} />;
        }

        if (!values.email || values.email.trim() === '') {
            errors.email = <FormattedMessage id={'picking.order.show.customerShippingEdit.form.error.email.missing'} />;
        }

        return errors;
    };

    function getShipmentIri() {
        if (shipment?.['@id']) {
            return shipment['@id'];
        }
        if (shipment?.id) {
            return `/shipments/${shipment.id}`;
        }
        return null;
    }

    function getShipmentAddressIri() {
        if (shipment?.shipmentAddress?.['@id']) {
            return shipment.shipmentAddress['@id'];
        }
        if (shipment?.shipmentAddress?.id) {
            return `/shipment_addresses/${shipment.shipmentAddress.id}`;
        }
        return null;
    }

    return (
        <Formik
            initialValues={initialValues}
            validate={validate}
            validateOnChange={false}
            validateOnBlur={true}
            validateOnMount={true}
            enableReinitialize={true}
            onSubmit={async (values, {setSubmitting}) => {
                setSubmitting(true);
                setErrorMessageId('');

                try {
                    const shipmentAddressIri = getShipmentAddressIri();

                    if (shipmentAddressIri) {
                        await updateShipmentAddress(shipmentAddressIri, values);
                    } else {
                        const shipmentIri = getShipmentIri();
                        if (!shipmentIri) {
                            throw new Error('Missing shipment IRI');
                        }
                        await createShipmentAddress(shipmentIri, values);
                    }

                    setSubmitting(false);
                    onClose();
                    refreshOrder();
                } catch (e) {
                    setSubmitting(false);
                    setErrorMessageId('picking.order.show.customerShippingEdit.error');
                }
            }}
        >
            {({
                  handleSubmit,
                  isSubmitting,
                  isValid,
                  dirty,
                  resetForm,
              }) => (
                <Dialog
                    open={open}
                    onClose={onClose}
                    slotProps={{ backdrop: { style: { backgroundColor: 'rgba(51, 51, 51, 0.20)' } } }}
                    classes={{ paper: classes.paper }}
                >
                    <DialogContent>
                        <h2>
                            <FormattedMessage id={'picking.order.show.customerShippingEdit.title'} />
                        </h2>

                        {showRelayPointButton && (
                            <div className={classes.relayPointRow}>
                                <p>
                                    <strong><FormattedMessage id={'picking.order.show.customerShippingEdit.relayPoint.label'} /></strong>
                                    {' '}
                                    {shipment?.relayPoint}
                                    {shipment?.deliveryCompany ? ` - ${shipment.deliveryCompany}` : ''}
                                </p>
                                <Button
                                    className={classes.actionButton}
                                    onClick={() => setRelayPointPickerOpen(true)}
                                >
                                    <FormattedMessage id={'picking.order.show.customerShippingEdit.relayPoint.change'} />
                                </Button>
                            </div>
                        )}

                        <form
                            className={classes.form}
                            onSubmit={handleSubmit}
                        >
                            <FormikInput
                                labelTranslationId={'picking.order.show.customerShippingEdit.form.label.firstname'}
                                inputProps={{name: 'firstname', type: 'text'}}
                            />
                            <FormikInput
                                labelTranslationId={'picking.order.show.customerShippingEdit.form.label.lastname'}
                                inputProps={{name: 'lastname', type: 'text'}}
                            />
                            <FormikInput
                                labelTranslationId={'picking.order.show.customerShippingEdit.form.label.company'}
                                inputProps={{name: 'company', type: 'text'}}
                            />
                            <FormikInput
                                labelTranslationId={'picking.order.show.customerShippingEdit.form.label.address'}
                                inputProps={{name: 'address', type: 'text'}}
                            />
                            <FormikInput
                                labelTranslationId={'picking.order.show.customerShippingEdit.form.label.addressMore'}
                                inputProps={{name: 'addressMore', type: 'text'}}
                            />
                            <FormikInput
                                labelTranslationId={'picking.order.show.customerShippingEdit.form.label.postalCode'}
                                inputProps={{name: 'postalCode', type: 'text'}}
                            />
                            <FormikInput
                                labelTranslationId={'picking.order.show.customerShippingEdit.form.label.city'}
                                inputProps={{name: 'city', type: 'text'}}
                            />
                            <FormikInput
                                labelTranslationId={'picking.order.show.customerShippingEdit.form.label.country'}
                                inputProps={{name: 'country', type: 'text'}}
                            />
                            <FormikInput
                                labelTranslationId={'picking.order.show.customerShippingEdit.form.label.phone'}
                                inputProps={{name: 'phone', type: 'text'}}
                            />
                            <FormikInput
                                labelTranslationId={'picking.order.show.customerShippingEdit.form.label.email'}
                                inputProps={{name: 'email', type: 'text'}}
                            />
                        </form>
                        {errorMessageId && (
                            <div className={classes.errorMessage}>
                                <FormattedMessage id={errorMessageId} />
                            </div>
                        )}
                    </DialogContent>
                    <DialogActions>
                        <Button
                            className={classes.actionButton}
                            onClick={() => {
                                resetForm();
                                onClose();
                            }}
                        >
                            <FormattedMessage id={'picking.order.show.customerShippingEdit.action.cancel'} />
                        </Button>
                        <Button
                            className={classNames(classes.actionButton, classes.validatedButton)}
                            disabled={isSubmitting || !dirty || !isValid}
                            onClick={handleSubmit}
                        >
                            <FormattedMessage id={'picking.order.show.customerShippingEdit.action.confirm'} />
                        </Button>
                    </DialogActions>

                    {showRelayPointButton && (
                        <ModalRelayPointPicker
                            open={relayPointPickerOpen}
                            onClose={() => setRelayPointPickerOpen(false)}
                            shipment={shipment}
                            onRelayPointChanged={() => {
                                // The API rewrote the shipment address itself: drop the local form
                                // state and reload the order so both modals show the new relay.
                                setRelayPointPickerOpen(false);
                                resetForm();
                                onClose();
                                refreshOrder();
                            }}
                        />
                    )}
                </Dialog>
            )}
        </Formik>
    );
}

ModalShipmentAddressEdit.defaultProps = {
    shipment: null,
};

ModalShipmentAddressEdit.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    shipment: PropTypes.object,
    refreshOrder: PropTypes.func.isRequired,
};
